// pricing-calculator/hooks/useTotals.ts
import { useMemo } from 'react';
import type {
  CalculatorSpec,
  CalculatorState,
  CalculatorTotals,
  Module,
  TierId,
} from '@dopomogai/types';
import { getSelectedModules, computeModuleSubtotalWithPreset } from '../utils/selection';
import { computeEtaDays } from '../utils/eta';
import { addRange, clampRangeToMax, daysToWeeks, scaleRange } from '../utils/range';
import { DEFAULT_CAPS } from '@dopomogai/types';

const TIER_ORDER: TierId[] = ['trainee', 'junior', 'middle', 'senior'];
const getHighestTier = (modules: Module[]): TierId => {
  if (!modules.length) return 'trainee';
  const maxIndex = Math.max(...modules.map((m) => TIER_ORDER.indexOf(m.tierGate)));
  return TIER_ORDER[maxIndex] || 'trainee';
};

export function useTotals(spec: CalculatorSpec, state: CalculatorState): {
  platform: ReturnType<typeof getPlatform>;
  selectedModules: Module[];
  totals: CalculatorTotals;
} {
  const platform = useMemo(() => getPlatform(spec, state), [spec, state.platformId]);

  const selectedModules = useMemo(
    () => getSelectedModules(spec.groups, state.selectedModuleIds),
    [spec.groups, state.selectedModuleIds]
  );

  const totals = useMemo<CalculatorTotals>(() => {
    // Base subtotals
    const platformAdj = platform?.priceAdj || 0;
    const moduleSubtotal = computeModuleSubtotalWithPreset(
      spec.groups,
      state.selectedModuleIds,
      state.presetId ? spec.presets?.find((p) => p.id === state.presetId) : undefined
    );
    let totalPrice = spec.base.priceUSD + platformAdj + moduleSubtotal;

    // Impacts
    const convLift = selectedModules.reduce((acc, f) => addRange(acc, f.convLiftPp), spec.base.convLiftPp);
    const supportReduction = selectedModules.reduce(
      (acc, f) => addRange(acc, f.supportReductionPct),
      spec.base.supportReductionPct
    );
    const caps = {
      convLiftPpMax: spec.caps?.convLiftPpMax ?? DEFAULT_CAPS.convLiftPpMax,
      supportReductionPctMax: spec.caps?.supportReductionPctMax ?? DEFAULT_CAPS.supportReductionPctMax,
    };
    const convLiftCapped = clampRangeToMax(convLift, caps.convLiftPpMax);
    const supportReductionCapped = clampRangeToMax(supportReduction, caps.supportReductionPctMax);

    // ETA (days)
    let etaDays = computeEtaDays(spec.base, selectedModules, platform);

    // Apply segment multipliers if present
    const segKey = state.segmentKey;
    if (segKey && Array.isArray(spec.segments)) {
      const seg = spec.segments.find((s) => s.key === segKey);
      if (seg) {
        const pm = typeof seg.price_multiplier === 'number' ? seg.price_multiplier : 1;
        const tm = typeof seg.time_multiplier === 'number' ? seg.time_multiplier : 1;
        totalPrice = Math.round(totalPrice * pm);
        etaDays = [Math.round(etaDays[0] * tm), Math.round(etaDays[1] * tm)];
      }
    }

    const etaWeeks = daysToWeeks(etaDays);
    const currentTier = getHighestTier(selectedModules);

    return {
      totalPriceUSD: totalPrice,
      etaDays,
      etaWeeks,
      convLiftPp: convLiftCapped,
      supportReductionPct: supportReductionCapped,
      currentTier,
    };
  }, [spec, platform, state.presetId, state.selectedModuleIds, selectedModules, state.segmentKey]);

  return { platform, selectedModules, totals };
}

function getPlatform(spec: CalculatorSpec, state: CalculatorState) {
  return spec.platforms.find((p) => p.id === state.platformId) || spec.platforms[0];
}
